import React, { useMemo } from 'react';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { IFavoriteNeighbourhood } from 'store/sagas/apiService/types';
import { RootAction, State } from 'store/state';
import { investorFavoriteNeighbourhoodsSelector } from 'store/state/domainData/selectors/investors';
import { toggleFavoriteHood } from 'store/state/app/actions';
import styled from '@emotion/styled';
import SaveIcon from 'assets/svg/investors-neighbourhood/save.svg';
import SaveFilledIcon from 'assets/svg/investors-neighbourhood/save-filled.svg';
import { useLocale } from 'locale';
import { SmallText, Text } from 'ds/components/typography';

const Button = styled.div`
  cursor: pointer;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ theme }) => theme.colors.neutrals.white};
  border: 1px solid ${({ theme }) => theme.colors.neutrals.grey8};
`;

const WithTextButton = styled(Button)`
  width: auto;
  border-radius: 40px;
  padding-left: ${({ theme }) => theme.spacing(2)};
  flex-shrink: 0;
  margin-right: auto;
  margin-bottom: auto;
  border-color: ${({ theme }) => theme.colors.neutrals.grey6};
`;

const CompareButton = styled.div`
  ${({ theme }) => `
    margin: 0 ${theme.spacing(2)};
    padding: ${theme.spacing(2, 0)};
    border-bottom: 1px dashed ${theme.colors.neutrals.grey8};
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${theme.colors.neutrals.grey1};
    height: 57px;
    svg {
      margin-right: -9px;
    }
  `}
`;

interface Props {
  docId: string;
  favoriteNeighbourhoods: IFavoriteNeighbourhood[];
  toggleFavorite: (docId: string, isFavorite: boolean) => void;
  variant?: 'comparePage' | 'default' | 'withText';
}

const BaseFavoriteNeighbourhoodWidget: React.FC<Props> = props => {
  const { favoriteNeighbourhoods, toggleFavorite, docId, variant = 'default' } = props;
  const { t } = useLocale();

  const isFavorite = useMemo(() => favoriteNeighbourhoods && favoriteNeighbourhoods.some(item => item.doc.docId === docId), [ favoriteNeighbourhoods, docId ]);

  const handleSaveNeighbourhood = () => {
    toggleFavorite(docId, isFavorite);
  };

  const dataAuto = `investors_neighbourhood_save_hood_button_${docId}`;

  if (variant === 'comparePage') {
    return (
      <CompareButton data-auto={dataAuto} onClick={handleSaveNeighbourhood}>
        {isFavorite ? <SaveFilledIcon /> : <SaveIcon />}
        <SmallText>{t('investors.neighbourhood.compare.overview.addFavoriteAction')}</SmallText>
      </CompareButton>
    );
  }

  if (variant === 'withText') {
    return (
      <WithTextButton data-auto={dataAuto} onClick={handleSaveNeighbourhood}>
        {isFavorite ? <SaveFilledIcon /> : <SaveIcon />}
        <Text weight="medium">{t('investors.neighbourhood.compare.overview.addFavoriteAction')}</Text>
      </WithTextButton>
    );
  }

  return (
    <Button data-auto={dataAuto} onClick={handleSaveNeighbourhood}>
      {isFavorite ? <SaveFilledIcon /> : <SaveIcon />}
    </Button>
  );
};

const mapStateToProps = (state: State) => ({
  favoriteNeighbourhoods: investorFavoriteNeighbourhoodsSelector(state),
});

const mapDispatchToProps = (dispatch: Dispatch<RootAction>) => ({
  toggleFavorite: (docId: string, isFavorite: boolean) => dispatch(toggleFavoriteHood({ docId, isFavorite })),
});

export const FavoriteNeighbourhoodWidget = connect(mapStateToProps, mapDispatchToProps)(BaseFavoriteNeighbourhoodWidget);

